import { Currency } from "@/components/Currency";
import { useModals } from "@/features/modals/hooks";
import { getQuotationNumbers } from "@/features/orders/quotation/utils";
import { useActiveProject } from "@/features/project/ActiveProject";
import { type SearchQuotationResult } from "@repo/api";
import { ListView } from "@repo/skapa";
import { Icon, chevronRightSmallIcon, shoppingBagIcon } from "@repo/skapa";
import { OneLineInfo } from "@repo/ui";
import {
  ListViewItemWithActions,
  ListViewItemWithActionsCheckbox,
} from "@repo/ui";
import { LocalizedDateAndTime } from "@repo/ui";
import { SectionContainer } from "@repo/ui";
import { trackAnalytics } from "@repo/utils";
import { hasItems, isDefined } from "@repo/utils";

import { type AddQuotationState } from "./AddQuotationReducer";

interface SelectQuotationStepProps {
  state?: AddQuotationState;
  selectQuotations: (quotationId: string[]) => void;
  customerQuotations?: SearchQuotationResult[];
  isLoading: boolean;
}

export const QuotationResultList = ({
  state,
  selectQuotations,
  customerQuotations,
  isLoading,
}: SelectQuotationStepProps) => {
  const { opportunity } = useActiveProject();
  const { pushModal } = useModals();
  const quotationIds = getQuotationNumbers(opportunity);
  const quotations = state?.quotations
    ? state?.quotations
    : (customerQuotations ?? []);

  const onChange = (checked: boolean, item: SearchQuotationResult) => {
    const existing = state?.quotationNumbers?.find(
      (quotationNumber) => quotationNumber === item.quotationId
    );
    const newValue = existing
      ? state?.quotationNumbers?.filter((f) => f !== item.quotationId)
      : [...(state?.quotationNumbers ?? []), item.quotationId];
    selectQuotations(newValue ?? []);
  };

  const list = quotations
    ?.filter(isDefined)
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  return (
    <SectionContainer>
      {!isLoading && hasItems(list) && (
        <ListView id="quotation-result">
          {list.map((quotation) => (
            <ListViewItemWithActions
              key={quotation.quotationId}
              icon={shoppingBagIcon}
              title={quotation.quotationId}
              description={
                <div>
                  <OneLineInfo
                    info={[
                      quotation.status,
                      <Currency
                        key={quotation.quotationId}
                        value={quotation?.metaData?.totalPrice?.priceInclTax}
                        currency={quotation?.metaData?.totalPrice?.currencyCode}
                      />,
                    ]}
                  />
                  <OneLineInfo
                    small
                    info={[
                      <LocalizedDateAndTime
                        key="creationDate"
                        date={quotation?.createdAt}
                      />,
                      quotation?.metaData?.totalPrice?.type,
                      quotation.buCode,
                    ]}
                  />
                </div>
              }
              action={
                <>
                  <ListViewItemWithActionsCheckbox
                    subtle
                    label={quotation.quotationId}
                    id={`checkbox-${quotation.quotationId}`}
                    value={quotation.quotationId}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      onChange(e.target.checked, quotation);
                      trackAnalytics("change_quotation_number");
                    }}
                    checked={
                      quotationIds.includes(quotation.quotationId) ||
                      state?.quotationNumbers?.includes(quotation.quotationId)
                    }
                    defaultChecked={quotationIds?.includes(
                      quotation.quotationId
                    )}
                    disabled={quotationIds?.includes(quotation.quotationId)}
                  />
                  <Icon paths={chevronRightSmallIcon} />
                </>
              }
              onClick={() => {
                pushModal({
                  type: "QUOTATION",
                  quotationId: quotation.quotationId,
                });
                trackAnalytics("open_quotation_modal");
              }}
            />
          ))}
        </ListView>
      )}
    </SectionContainer>
  );
};
